import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState, AppDispatch } from '../store/store';
import { DataSourceService } from '../api/generated/services/DataSourceService';
import { DataSourceView } from '../api/generated/models/DataSourceView';
import { ApiError } from '../api/generated/core/ApiError';
import { addNotification } from '../store/slices/notificationSlice';
import {
  fetchBridgeStatus,
  fetchDataSource,
} from '../store/slices/dataSourceSlice';
import { helper } from '../utils/helper';
import Modal from './ui/Modal';
import {
  getWawiBridgeDownloadUrl,
  WAWI_BRIDGE_STATUS_POLL_MS,
} from '../config/wawiBridge';

/**
 * "Wawi Bridge" connection method card (Profile → Database connection).
 * Pairing itself happens exclusively inside the Windows exe - the web UI only
 * shows the pitch/download when not paired and live status + test/disconnect
 * when paired.
 */
const WawiBridgeSection: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const dataSource = useSelector((state: RootState) => state.dataSource);
  const contact = useSelector((state: RootState) => state.profile.contact);

  const isBridge = dataSource.data?.type === DataSourceView.type.BRIDGE;
  const bridge = isBridge ? dataSource.data?.bridge : null;
  const isPaired = bridge?.paired === true;

  // The download link is keyed by the account owner's user id (for a subuser
  // session - the main account they act on behalf of).
  const ownerUserId = contact?.parentUserId || contact?.id;
  const downloadUrl = ownerUserId ? getWawiBridgeDownloadUrl(ownerUserId) : null;

  const [isTesting, setIsTesting] = useState(false);
  const [isRevokeModalOpen, setIsRevokeModalOpen] = useState(false);
  const [isRevoking, setIsRevoking] = useState(false);

  // The URL itself is intentionally not rendered (long and scary) - copying is
  // the primary flow, so confirm it with a toast.
  const handleCopyLink = () => {
    if (!downloadUrl) {
      return;
    }
    navigator.clipboard?.writeText(downloadUrl).then(() => {
      dispatch(
        addNotification({
          type: 'success',
          title: t('notification_success'),
          message: t('copiedToClipboard'),
          autoHideDuration: 3000,
        }),
      );
    });
  };

  // Poll while the card is on screen (the component is only mounted while the
  // "Database connection" tab is active). Skips ticks in background browser
  // tabs; no websockets by design.
  // - type=bridge: poll the live agent status.
  // - otherwise: re-fetch the DataSource itself - pairing happens inside the
  //   exe, so this is how the card flips to "Connected" (and the rest of the
  //   app unlocks) without a page reload.
  useEffect(() => {
    const tick = () => {
      if (document.hidden) {
        return;
      }
      if (isBridge) {
        dispatch(fetchBridgeStatus());
      } else {
        dispatch(fetchDataSource());
      }
    };
    tick();
    const id = window.setInterval(tick, WAWI_BRIDGE_STATUS_POLL_MS);
    return () => window.clearInterval(id);
  }, [isBridge, dispatch]);

  const handleTest = async () => {
    setIsTesting(true);
    try {
      await DataSourceService.postAppDataSourceBridgeTest();
      dispatch(
        addNotification({
          type: 'success',
          title: t('notification_success'),
          message: t('wawi_bridge_test_success'),
          autoHideDuration: 5000,
        }),
      );
    } catch (err) {
      let message = t('wawi_bridge_test_error');
      if (err instanceof ApiError) {
        const body = helper.parseApiErrorBody(err);
        // Branch by errorCode, not by text/status (project convention).
        if (body?.errorCode === 'bridge_not_paired') {
          message = t('wawi_bridge_error_not_paired');
        } else if (body?.errorCode === 'bridge_agent_offline') {
          message = t('wawi_bridge_error_agent_offline');
        } else if (body?.error) {
          message = body.error;
        }
      }
      dispatch(
        addNotification({
          type: 'error',
          title: t('notification_error'),
          message,
          autoHideDuration: 5000,
        }),
      );
    } finally {
      setIsTesting(false);
    }
  };

  const handleRevoke = async () => {
    setIsRevoking(true);
    try {
      await DataSourceService.postAppDataSourceBridgeRevoke();
      dispatch(
        addNotification({
          type: 'success',
          title: t('notification_success'),
          message: t('wawi_bridge_disconnect_success'),
        }),
      );
      setIsRevokeModalOpen(false);
      // The DataSource stays `bridge`, only `paired` flips to false - refetch
      // so the card falls back to the download/setup state.
      dispatch(fetchDataSource({ force: true }));
    } catch (err) {
      let message = t('wawi_bridge_disconnect_error');
      if (err instanceof ApiError) {
        const body = helper.parseApiErrorBody(err);
        if (body?.error) {
          message = body.error;
        }
      }
      dispatch(
        addNotification({
          type: 'error',
          title: t('notification_error'),
          message,
        }),
      );
    } finally {
      setIsRevoking(false);
    }
  };

  return (
    <div className="connection-method">
      <div className="connection-method__header">
        <h3>{t('wawi_bridge_title')}</h3>
        <span className="connection-method__badge">
          {t('wawi_bridge_recommended')}
        </span>
      </div>
      <p className="connection-method__text">{t('wawi_bridge_pitch')}</p>

      {isPaired ? (
        <>
          <div className="wawi-bridge-status">
            <span
              className={`wawi-bridge-status__pill ${
                bridge?.online
                  ? 'wawi-bridge-status__pill--online'
                  : 'wawi-bridge-status__pill--offline'
              }`}
            >
              {bridge?.online
                ? t('wawi_bridge_status_connected')
                : t('wawi_bridge_status_offline')}
            </span>
            <span className="wawi-bridge-status__details">
              {bridge?.online && bridge?.hostName && (
                <>{bridge.hostName} · </>
              )}
              {bridge?.online && bridge?.agentVersion && (
                <>v{bridge.agentVersion} · </>
              )}
              {bridge?.lastSeenAt && (
                <>
                  {t('wawi_bridge_status_last_seen')}:{' '}
                  {helper.formatDate(bridge.lastSeenAt)}
                </>
              )}
            </span>
          </div>
          <div className="form-actions">
            <button
              type="button"
              className="button button--secondary"
              onClick={handleTest}
              disabled={isTesting || isRevoking}
            >
              {isTesting ? t('notification_loading') : t('test_connection')}
            </button>
            <button
              type="button"
              className="delete-account-button"
              onClick={() => setIsRevokeModalOpen(true)}
              disabled={isTesting || isRevoking}
            >
              {t('wawi_bridge_disconnect_button')}
            </button>
          </div>
        </>
      ) : (
        <>
          {/* The primary use case: the customer copies the link on their
              office machine and opens it in a browser on the Windows server
              where JTL-Wawi runs (the endpoint needs no auth). */}
          {downloadUrl && (
            <div className="form-actions">
              <button
                type="button"
                className="button button--primary"
                onClick={handleCopyLink}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                </svg>
                {t('wawi_bridge_copy_link')}
              </button>
              <p className="connection-method__text wawi-bridge-download-hint">
                {t('wawi_bridge_copy_hint')}
              </p>
              <span className="wawi-bridge-download-or">
                {t('wawi_bridge_download_or')}
              </span>
              <a
                className="button button--primary"
                href={downloadUrl}
                download
              >
                {t('wawi_bridge_download_button')}
              </a>
            </div>
          )}
          <p className="connection-method__text">
            {t('wawi_bridge_setup_hint')}
          </p>
        </>
      )}

      <Modal
        isOpen={isRevokeModalOpen}
        hideCloseButton={true}
        isCentered={true}
        onClose={() => !isRevoking && setIsRevokeModalOpen(false)}
        title={t('wawi_bridge_disconnect_confirm_title')}
      >
        <div className="center">
          <p>{t('wawi_bridge_disconnect_confirm_message')}</p>
        </div>
        <div className="modal-buttons">
          <button
            className="modal-button cancel-delete"
            onClick={() => setIsRevokeModalOpen(false)}
            disabled={isRevoking}
          >
            {t('action_cancel')}
          </button>
          <button
            className="modal-button confirm-delete"
            onClick={handleRevoke}
            disabled={isRevoking}
          >
            {isRevoking
              ? t('notification_loading')
              : t('wawi_bridge_disconnect_button')}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default WawiBridgeSection;
